import { useAuth } from "./contexts/AuthContext";
import { UserCircleIcon } from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { useState, useRef, useEffect } from "react";
import { useUrlParams } from "./utils/url";
import { twMerge } from "tailwind-merge";
import { Roles } from "./shared/abilities";

interface HeaderProps {
  isAdminPage?: boolean;
  showLogo?: boolean;
}

const Header = ({ isAdminPage, showLogo = false }: HeaderProps) => {
  const { user, logout, organizations } = useAuth();
  const [ dropdownOpen, setDropdownOpen ] = useState(false);
  const [ orgDropdownOpen, setOrgDropdownOpen ] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const params = useUrlParams();
  const currentOrgId = params.org_id;

  const isMultiOrg = organizations.length > 1;
  const showOrgSelect = isAdminPage && isMultiOrg;
  const currentOrg = organizations.find((org) => String(org.id) === String(currentOrgId)) ?? organizations[0];

  const handleOrgChange = (newOrgId: string) => {
    if (newOrgId === 'manage') {
      window.location.href = '/organizations';
      return;
    }

    const newPath = window.location.pathname.replace(/^\/[^/]+\//, `/${newOrgId}/`);
    window.location.href = newPath;
  };

  const handleLogout = () => {
    logout().then(() => {
      setDropdownOpen(false);
      window.location.href = `/${organizations.length > 0 ? `${organizations[0].id}/` : ''}login`;
    }).catch((error) => {
      console.error('Error logging out:', error);
    });
  };

  useEffect(() => {
    const handleClickOutsideOrg = (event: MouseEvent) => {
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setOrgDropdownOpen(false);
      }
    };

    const handleClickOutsideUser = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutsideOrg);
    document.addEventListener("mousedown", handleClickOutsideUser);
    return () => {
      document.removeEventListener("mousedown", handleClickOutsideOrg);
      document.removeEventListener("mousedown", handleClickOutsideUser);
    };
  }, []);

  const headerColor = showLogo ? 'bg-gray-200 color-black border-b-4 border-brand-600' : 'bg-brand-600';
  const dropdownColor = showLogo ? 'text-gray-800' : 'text-white';

  return (
    <div className={headerColor}>
      <div className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex justify-between items-center">
          {showLogo && (
            <div className="flex items-center text-white font-bold text-2xl">
              <img src="/Patient360.svg" alt="Patient360" className="w-24" />
            </div>
          )}
          <div className="flex items-center">
            {showOrgSelect && currentOrg && (
              <div className="relative" ref={orgDropdownRef}>
                <button
                  type="button"
                  onClick={() => setOrgDropdownOpen(!orgDropdownOpen)}
                  className="flex items-center gap-2 w-48 lg:w-64 min-w-0 px-3 py-2 rounded-lg bg-white text-gray-900 border border-gray-300 font-medium text-sm hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 focus:ring-offset-brand-600"
                  aria-expanded={orgDropdownOpen}
                  aria-haspopup="listbox"
                >
                  <div className="flex flex-col items-start min-w-0">
                    <span className="text-sm font-semibold truncate">
                      {currentOrg.name}
                    </span>
                    <span className="text-xs text-gray-500 truncate">
                      ID: {currentOrg.id}
                    </span>
                  </div>
                  <ChevronDownIcon className="h-5 w-5 flex-shrink-0 text-gray-500 ml-auto" />
                </button>
                {orgDropdownOpen && (
                  <ul
                    className="absolute mt-0.5 w-48 lg:w-64 min-w-0 overflow-auto rounded-lg border border-gray-200 bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-20"
                    role="listbox"
                  >
                    {organizations
                      .filter((org) => org.user_role !== Roles.BASIC_USER)
                      .map((org) => (
                        <li
                          key={org.id}
                          role="option"
                          aria-selected={String(org.id) === String(currentOrgId)}
                          className={`cursor-pointer px-4 py-2 text-sm flex items-center border-b border-gray-100 ${
                            String(org.id) === String(currentOrgId)
                              ? "bg-brand-50 font-medium text-brand-700"
                              : "text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                          }`}
                          onClick={() => {
                            handleOrgChange(String(org.id));
                            setOrgDropdownOpen(false);
                          }}
                        >
                          <div className="flex flex-col items-start">
                            <span className="truncate">{org.name}</span>
                            <span className="text-xs text-gray-500 truncate">ID: {org.id}</span>
                          </div>
                        </li>
                    ))}
                    <li
                      role="option"
                      aria-selected="false"
                      className="cursor-pointer px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100"
                      onClick={() => {
                        handleOrgChange("manage");
                        setOrgDropdownOpen(false);
                      }}
                    >
                      Manage Organizations
                    </li>
                  </ul>
                )}
              </div>
            )}
          </div>
          <div className="relative" ref={userDropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={twMerge("font-medium px-4 rounded-md transition-colors flex items-center gap-2", dropdownColor)}
            >
              <span className="hidden lg:inline">{user?.email}</span> <UserCircleIcon className="w-8 h-8" />
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-0.5 w-56 bg-white rounded-md shadow-lg z-10">
                <div className="py-2 px-4 block lg:hidden border-b border-gray-100">
                  <span className="block text-xs break-all">
                    {user?.email}
                  </span>
                </div>
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    Logout
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
